import React, { useState } from 'react';
import { Shield, X, AlertCircle } from 'lucide-react';
import { Role } from '../types';
import { usersAPI } from '../services/api';

interface RequestRoleModalProps {
    roles: Role[];
    onClose: () => void;
    onSuccess: () => void;
}

const RequestRoleModal: React.FC<RequestRoleModalProps> = ({ roles, onClose, onSuccess }) => {
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (selectedRoles.length === 0) {
            setError('Please select at least one role to request.');
            return;
        }
        setLoading(true);
        try {
            await usersAPI.requestRole(selectedRoles);
            onSuccess();
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to request roles.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (roleId: string) => {
        if (selectedRoles.includes(roleId)) {
            setSelectedRoles(selectedRoles.filter(r => r !== roleId));
        } else {
            setSelectedRoles([...selectedRoles, roleId]);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="glass rounded-2xl shadow-2xl w-full max-w-md p-6 animate-fade-in max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-white" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Request Roles</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Select the roles you would like to request access for. An admin will review your request.
                    </p>

                    <div className="space-y-2 mt-4">
                        {roles.filter(r => r.id !== 'admin').map(role => (
                            <label
                                key={role.id}
                                className="flex items-start space-x-3 p-3 rounded-xl cursor-pointer hover:bg-white/30 dark:hover:bg-gray-800/30 transition-all border border-transparent hover:border-gray-200 dark:hover:border-gray-700"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedRoles.includes(role.id)}
                                    onChange={() => handleToggle(role.id)}
                                    className="w-4 h-4 mt-0.5 rounded border-gray-300 text-primary focus:ring-primary/50 accent-purple-600"
                                />
                                <div className="flex-1 min-w-0">
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">{role.name}</span>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{role.description}</p>
                                </div>
                            </label>
                        ))}
                    </div>

                    {error && (
                        <div className="flex items-center space-x-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-xl p-3 text-sm">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="flex space-x-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || selectedRoles.length === 0}
                            className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white font-medium hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:scale-100"
                        >
                            {loading ? 'Submitting...' : 'Request Access'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RequestRoleModal;
